import { Controller } from "@hotwired/stimulus";

// Stimulus controller to show guided onboarding stickers pointing to UI elements
export default class extends Controller {
  static targets = [
    "overlay",
    "sticker",
    "title",
    "description",
    "nextButton",
    "prevButton",
    "finishButton",
    "stepIndicator",
  ];

  static values = {
    steps: Array,
    start: Number,
    total: Number,
    key: String,
    finishBehavior: String,
    debug: Boolean,
  };

  connect() {
    // Lift the tour out of any ancestor stacking context (e.g. <main> has
    // position:relative + z-index, which would otherwise paint the fixed
    // overlay/sticker *below* the sticky navbar). Moving the element re-fires
    // connect() with the element now parented to <body>, where we initialize.
    if (this.element.parentElement !== document.body) {
      document.body.appendChild(this.element);
      return;
    }

    // Debug initial state
    this.log("connect", {
      stepsLength: this.stepsValue ? this.stepsValue.length : 0,
      key: this.hasKeyValue ? this.keyValue : null,
      finishBehavior: this.hasFinishBehaviorValue ? this.finishBehaviorValue : "complete_global",
    });

    this.initializeTour();
  }

  async initializeTour() {
    // If key provided, cross-check server and local gating
    if (this.hasKeyValue && this.keyValue && window.localStorage) {
      let serverCompleted = null;

      // Check if we have a recent cached server response
      const cacheKey = "onboarding_tours_server_cache";
      const cacheTimestampKey = "onboarding_tours_server_cache_timestamp";
      const cacheValidDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      try {
        const cachedData = window.localStorage.getItem(cacheKey);
        const cachedTimestamp = window.localStorage.getItem(cacheTimestampKey);
        
        if (cachedData && cachedTimestamp) {
          const cacheAge = Date.now() - parseInt(cachedTimestamp);
          if (cacheAge < cacheValidDuration) {
            const serverList = JSON.parse(cachedData);
            serverCompleted = serverList.includes(this.keyValue);
            this.log("gating_server_cached", { serverList, serverCompleted, cacheAge });
          }
        }
        
        // Only make server request if we don't have valid cached data
        if (serverCompleted === null) {
          const res = await fetch("/home/onboarding/tours", { credentials: "same-origin", headers: { "X-Requested-With": "XMLHttpRequest" } });
          if (res.ok) {
            const data = await res.json();
            const serverList = Array.isArray(data.completed_tours) ? data.completed_tours : [];
            serverCompleted = serverList.includes(this.keyValue);
            
            // Cache the server response
            window.localStorage.setItem(cacheKey, JSON.stringify(serverList));
            window.localStorage.setItem(cacheTimestampKey, Date.now().toString());
            
            this.log("gating_server", { serverList, serverCompleted });
          }
        }
      } catch (_e) {
        this.log("gating_server_error", {});
      }

      const localCompleted = this._registryHas(this.keyValue);
      this.log("gating_local", { localCompleted, registry: this._readRegistry() });

      // If server says completed, trust server and sync local, then skip
      if (serverCompleted === true) {
        if (!localCompleted) this._registryAdd(this.keyValue);
        this.log("gating_skip_server", {});
        this.element.remove();
        return;
      }

      // If server says not completed (false), but local says completed, clear local so we can show
      if (serverCompleted === false && localCompleted) {
        this._registryRemove(this.keyValue);
        this.log("gating_clear_local_due_to_server", {});
      }

      // If server unavailable (null) and local says completed, skip
      if (serverCompleted === null && localCompleted) {
        this.log("gating_skip_local_only", {});
        this.element.remove();
        return;
      }
    }

    this.currentIndex = (this.startValue || 0);
    this.totalSteps = this.totalValue || (this.stepsValue ? this.stepsValue.length : 0);
    this._boundOnResize = this.positionSticker.bind(this);
    this._boundOnScroll = () => {
      if (this.stepsValue && this.stepsValue[this.currentIndex]) {
        this.positionSticker(this.stepsValue[this.currentIndex]);
      }
    };
    window.addEventListener("resize", this._boundOnResize);
    window.addEventListener("scroll", this._boundOnScroll, { passive: true });
    if (window.visualViewport) {
      this._vvResize = () => this._boundOnScroll();
      this._vvScroll = () => this._boundOnScroll();
      window.visualViewport.addEventListener("resize", this._vvResize, { passive: true });
      window.visualViewport.addEventListener("scroll", this._vvScroll, { passive: true });
    }
    if (!this.stepsValue || this.stepsValue.length === 0) {
      this.log("no_steps", {});
    }
    this.showCurrentStep();
  }

  disconnect() {
    window.removeEventListener("resize", this._boundOnResize);
    window.removeEventListener("scroll", this._boundOnScroll);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._vvResize);
      window.visualViewport.removeEventListener("scroll", this._vvScroll);
    }
    if (this._repositionTimer) {
      clearTimeout(this._repositionTimer);
      this._repositionTimer = null;
    }
  }

  next() {
    if (this.currentIndex < this.totalSteps - 1) {
      this.currentIndex += 1;
      this.showCurrentStep();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.showCurrentStep();
    }
  }

  finish() {
    // Mark page-specific completion if key provided
    if (this.hasKeyValue && this.keyValue && window.localStorage) {
      this._registryAdd(this.keyValue);
      // Clear server cache since we're updating completion status
      this._clearServerCache();
    }

    const behavior = this.hasFinishBehaviorValue ? this.finishBehaviorValue : "complete_global";
    this.log("finish", { behavior, key: this.hasKeyValue ? this.keyValue : null });

    if (behavior === "complete_global") {
      const tokenEl = document.querySelector('[name="csrf-token"]');
      const csrf = tokenEl ? tokenEl.content : null;
      const headers = {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };
      if (csrf) headers["X-CSRF-Token"] = csrf;

      fetch("/home/onboarding/step", {
        method: "PATCH",
        headers,
        body: JSON.stringify({ step: 5 }),
        credentials: "same-origin",
      }).catch(() => {}).finally(() => this._teardown());
    } else {
      this._teardown();
    }

    // Persist tour completion by key to backend when available
    if (this.hasKeyValue && this.keyValue) {
      const tokenEl2 = document.querySelector('[name="csrf-token"]');
      const csrf2 = tokenEl2 ? tokenEl2.content : null;
      const headers2 = {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };
      if (csrf2) headers2["X-CSRF-Token"] = csrf2;
      fetch("/home/onboarding/complete_tour", {
        method: "POST",
        headers: headers2,
        body: JSON.stringify({ key: this.keyValue }),
        credentials: "same-origin",
      }).catch(() => {});
    }
  }

  _storageKey() {
    return `onboarding_tour_completed:${this.keyValue}`;
  }

  _registryKey() {
    return "onboarding_tours_completed";
  }

  _readRegistry() {
    try {
      const raw = window.localStorage.getItem(this._registryKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  _writeRegistry(arrayOfKeys) {
    try {
      window.localStorage.setItem(this._registryKey(), JSON.stringify(arrayOfKeys));
    } catch (_e) {
      // ignore
    }
  }

  _registryHas(key) {
    // Backward compatibility: also honor legacy per-key flag
    const legacy = window.localStorage.getItem(`onboarding_tour_completed:${key}`) === "true";
    if (legacy) return true;
    const list = this._readRegistry();
    return list.includes(key);
  }

  _registryAdd(key) {
    // Write both new registry and legacy key for compatibility
    try {
      window.localStorage.setItem(`onboarding_tour_completed:${key}`, "true");
    } catch (_e) {}
    const list = this._readRegistry();
    if (!list.includes(key)) {
      list.push(key);
      this._writeRegistry(list);
    }
  }

  _clearServerCache() {
    try {
      window.localStorage.removeItem("onboarding_tours_server_cache");
      window.localStorage.removeItem("onboarding_tours_server_cache_timestamp");
    } catch (_e) {
      // ignore storage errors
    }
  }

  _registryRemove(key) {
    try {
      window.localStorage.removeItem(`onboarding_tour_completed:${key}`);
    } catch (_e) {}
    const list = this._readRegistry().filter((k) => k !== key);
    this._writeRegistry(list);
  }

  showCurrentStep() {
    if (!this.stepsValue || this.stepsValue.length === 0) return;

    // Find the next available step whose selector exists and is visible in DOM
    let attempts = 0;
    let step = this.stepsValue[this.currentIndex];
    while (attempts < this.totalSteps) {
      const anchor = this._resolveAnchor(step);
      this.log("check_step", { index: this.currentIndex, selector: step ? step.selector : null, anchorFound: !!anchor });
      if (anchor) break;
      this.currentIndex = (this.currentIndex + 1) % this.totalSteps;
      step = this.stepsValue[this.currentIndex];
      attempts += 1;
    }

    // If none of the steps have valid anchors, close the tour
    const validAnchor = this._resolveAnchor(step);
    if (!validAnchor) {
      this.log("no_valid_anchors", {});
      this._teardown();
      return;
    }
    // Update content
    if (this.hasTitleTarget) this.titleTarget.textContent = step.title || "";
    if (this.hasDescriptionTarget) this.descriptionTarget.textContent = step.description || "";

    // Update progress indicator
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent = `${this.currentIndex + 1} / ${this.totalSteps}`;
    }

    // Toggle buttons
    if (this.hasPrevButtonTarget) this.prevButtonTarget.disabled = this.currentIndex === 0;
    if (this.hasNextButtonTarget) this.nextButtonTarget.style.display = (this.currentIndex < this.totalSteps - 1) ? "inline-flex" : "none";
    if (this.hasFinishButtonTarget) this.finishButtonTarget.style.display = (this.currentIndex === this.totalSteps - 1) ? "inline-flex" : "none";

    // Position sticker near the target element and gently scroll into view
    this.log("show_step", { index: this.currentIndex, selector: step.selector });
    this._scrollAnchorIntoView(validAnchor, step);
    this.positionSticker(step);
    if (this._repositionTimer) clearTimeout(this._repositionTimer);
    this._repositionTimer = setTimeout(() => {
      this.log("reposition_after_scroll", {});
      this.positionSticker(step);
    }, 350);
  }

  positionSticker(step) {
    const anchor = this._resolveAnchor(step);
    const sticker = this.stickerTarget;
    const overlay = this.overlayTarget;

    if (!anchor) {
      this.log("position_no_anchor", { selector: step ? step.selector : null });
      sticker.style.display = "none";
      overlay.style.display = "none";
      return;
    }

    document.body.classList.add("onboarding-tour--active");
    overlay.style.display = "block";
    sticker.style.display = "block";

    const rect = anchor.getBoundingClientRect();
    const stickerRect = sticker.getBoundingClientRect();

    const margin = 12;
    const isMobile = window.innerWidth <= 640;
    const extraGap = (step && typeof step.gap === "number") ? step.gap : (isMobile ? 20 : 8);
    // Sticker/overlay are position:fixed, so work in viewport coordinates
    // (no scroll offsets) — this keeps placement independent of any
    // positioned/transformed ancestor that would otherwise be the
    // containing block for absolute positioning.
    const viewport = {
      left: margin,
      top: margin,
      right: window.innerWidth - margin,
      bottom: window.innerHeight - margin,
    };

    // Candidate placements (ordered by preference): can be overridden per-step via step.placement
    const pref = (step && step.placement) ? step.placement : "auto";
    const order = (pref === "bottom") ? ["bottom","right","left","top"]
                 : (pref === "top") ? ["top","right","left","bottom"]
                 : (pref === "left") ? ["left","bottom","top","right"]
                 : (pref === "right") ? ["right","bottom","top","left"]
                 : ["right","bottom","left","top"];

    const place = (where) => {
      switch(where) {
        case "right":
          return { top: rect.top, left: rect.right + margin };
        case "bottom":
          return { top: rect.bottom + margin + extraGap, left: rect.left };
        case "left":
          return { top: rect.top, left: rect.left - stickerRect.width - margin };
        case "top":
          return { top: rect.top - stickerRect.height - margin - extraGap, left: rect.left };
      }
    };

    const candidates = order.map(place);

    let placed = null;
    for (const pos of candidates) {
      const fits = (
        pos.left >= viewport.left &&
        pos.top >= viewport.top &&
        (pos.left + stickerRect.width) <= viewport.right &&
        (pos.top + stickerRect.height) <= viewport.bottom
      );
      if (fits) { placed = pos; break; }
    }

    if (!placed) {
      // Clamp within viewport (prefer bottom-like alignment)
      const base = place("bottom");
      const clamped = {
        left: Math.min(Math.max(base.left, viewport.left), viewport.right - stickerRect.width),
        top: Math.min(Math.max(base.top, viewport.top), viewport.bottom - stickerRect.height),
      };
      placed = clamped;
      this.log("position_clamped", placed);
    }

    this.log("position", { rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height }, top: placed.top, left: placed.left });
    sticker.style.top = `${placed.top}px`;
    sticker.style.left = `${placed.left}px`;

    // If bottom placement desired ensure extra breathing room: scroll more if sticker is too close to bottom
    if ((pref === "bottom" || order[0] === "bottom")) {
      const minBottomPadding = isMobile ? 48 : 24; // ensure sticker bottom has room above screen bottom
      const bottomSpace = viewport.bottom - (placed.top + stickerRect.height);
      const need = minBottomPadding - bottomSpace;
      if (need > 0) {
        window.scrollBy({ top: need, behavior: "smooth" });
        if (this._repositionTimer) clearTimeout(this._repositionTimer);
        this._repositionTimer = setTimeout(() => {
          this.log("reposition_after_extra_padding", { need });
          this.positionSticker(step);
        }, 350);
      }
    }

    // Draw a focus ring around the target using CSS vars on overlay (viewport coords; no clamp necessary for ring)
    overlay.style.setProperty("--focus-top", `${rect.top - 8}px`);
    overlay.style.setProperty("--focus-left", `${rect.left - 8}px`);
    overlay.style.setProperty("--focus-width", `${rect.width + 16}px`);
    overlay.style.setProperty("--focus-height", `${rect.height + 16}px`);
  }

  _resolveAnchor(step) {
    if (!step) return null;
    const selectors = [ step.selector, ...(step.altSelectors || []) ].filter(Boolean);
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (this._isVisible(el)) return el;
    }
    return null;
  }

  _isVisible(el) {
    if (!el) return false;
    if (el.getClientRects().length === 0) return false; // not rendered
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
    return true;
  }

  _teardown() {
    this.log("teardown", {});
    this.element.remove();
    document.body.classList.remove("onboarding-tour--active");
  }

  log(event, payload) {
    if (this.hasDebugValue && this.debugValue) {
      // eslint-disable-next-line no-console
      console.debug("[onboarding-tour]", event, payload || {});
    }
  }

  _scrollAnchorIntoView(anchor, step) {
    const rect = anchor.getBoundingClientRect();
    const vv = window.visualViewport;
    const viewportTop = vv ? vv.pageTop : window.scrollY;
    const viewportHeight = vv ? vv.height : window.innerHeight;
    const targetTopAbs = window.scrollY + rect.top;
    const targetCenterAbs = targetTopAbs + rect.height / 2;

    // Optional header offset if page uses a fixed header; can be passed per-step
    const headerOffset = (step && typeof step.headerOffset === "number") ? step.headerOffset : 0;

    // Desired scroll so the input is vertically centered
    let desired = targetCenterAbs - (viewportHeight / 2) - headerOffset;
    desired = Math.max(0, desired);
    this.log("custom_scroll", { desired, viewportTop, viewportHeight, headerOffset });
    window.scrollTo({ top: desired, behavior: "smooth" });
  }
}


